import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import toast, { Toaster } from 'react-hot-toast';
import { customToast } from '../../utils/toast';
import {
  BuildingOfficeIcon,
  UserGroupIcon,
  BriefcaseIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  ClockIcon,
  StarIcon,
  UserPlusIcon,
  MapPinIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

// Import RTK Query hooks
import {
  useGetDepartmentsQuery,
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useDeleteDepartmentMutation,
} from '../../services/departmentApi';

import {
  useGetDesignationsQuery,
  useCreateDesignationMutation,
  useUpdateDesignationMutation,
  useDeleteDesignationMutation,
} from '../../services/designationApi';

import {
  useGetBranchesQuery,
  useCreateBranchMutation,
  useUpdateBranchMutation,
  useDeleteBranchMutation,
} from '../../services/branchApi';

import {
  useGetShiftsQuery,
  useCreateShiftMutation,
  useUpdateShiftMutation,
  useDeleteShiftMutation,
  useSetDefaultShiftMutation,
  useAssignShiftToEmployeeMutation,
  useUnassignShiftFromEmployeeMutation,
} from '../../services/shiftApi';

// Import Redux actions
import {
  setDepartments,
  setFilters as setDeptFilters,
} from '../../store/slices/departmentSlice';

import {
  setDesignations,
  setFilters as setDesigFilters,
} from '../../store/slices/designationSlice';

import {
  setBranches,
  setFilters as setBranchFilters,
} from '../../store/slices/branchSlice';

import {
  setShifts,
  setFilters as setShiftFilters,
} from '../../store/slices/shiftSlice';

const Master = () => {
  const { t } = useTranslation(['common', 'master']);
  const dispatch = useDispatch();

  // Active tab state
  const [activeTab, setActiveTab] = useState('branches'); // Default to branches since that's what we updated

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Form states
  const [formData, setFormData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Geolocation state
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');

  // Helper functions for auto-generating codes based on onboarding logic
  const generateBranchCode = (branchName, existingBranches = []) => {
    if (!branchName?.trim()) return '';

    // Based on onboarding pattern: get first letters of each word, up to 2 letters max
    const words = branchName.trim().split(/\s+/);
    const acronym = words
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substring(0, 2);

    // Get current date in YYMM format
    const now = new Date();
    const year = String(now.getFullYear()).slice(-2);
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const dateStr = `${year}${month}`;

    // Ensure uniqueness by checking existing codes
    let baseCode = `${acronym}${dateStr}`;
    let counter = 1;
    let uniqueCode = baseCode;
    const existingCodes = existingBranches.map(branch => branch.code?.toLowerCase());

    while (existingCodes.includes(uniqueCode.toLowerCase())) {
      uniqueCode = `${acronym}${dateStr}${String(counter).padStart(2, '0')}`;
      counter++;
    }

    return uniqueCode;
  };

  const generateShiftCode = (shiftName, existingShifts = []) => {
    if (!shiftName?.trim()) return '';

    // Get first letters of each word, up to 3 letters max for shifts
    const words = shiftName.trim().split(/\s+/);
    const acronym = words
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substring(0, 3);

    // Get current date in YYMM format
    const now = new Date();
    const year = String(now.getFullYear()).slice(-2);
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const dateStr = `${year}${month}`;

    // Ensure uniqueness by checking existing codes
    let baseCode = `${acronym}${dateStr}`;
    let counter = 1;
    let uniqueCode = baseCode;
    const existingCodes = existingShifts.map(shift => shift.code?.toLowerCase());

    while (existingCodes.includes(uniqueCode.toLowerCase())) {
      uniqueCode = `${acronym}${dateStr}${String(counter).padStart(2, '0')}`;
      counter++;
    }

    return uniqueCode;
  };

  // RTK Query hooks
  const {
    data: departmentsData,
    isLoading: deptLoading,
    error: deptError,
    refetch: refetchDepartments,
  } = useGetDepartmentsQuery();

  const {
    data: designationsData,
    isLoading: desigLoading,
    error: desigError,
    refetch: refetchDesignations,
  } = useGetDesignationsQuery();

  const {
    data: branchesData,
    isLoading: branchLoading,
    error: branchError,
    refetch: refetchBranches,
  } = useGetBranchesQuery();

  // Mutation hooks
  const [createDepartment] = useCreateDepartmentMutation();
  const [updateDepartment] = useUpdateDepartmentMutation();
  const [deleteDepartment] = useDeleteDepartmentMutation();

  const [createDesignation] = useCreateDesignationMutation();
  const [updateDesignation] = useUpdateDesignationMutation();
  const [deleteDesignation] = useDeleteDesignationMutation();

  const [createBranch] = useCreateBranchMutation();
  const [updateBranch] = useUpdateBranchMutation();
  const [deleteBranch] = useDeleteBranchMutation();

  const {
    data: shiftsData,
    isLoading: shiftLoading,
    error: shiftError,
    refetch: refetchShifts,
  } = useGetShiftsQuery();

  // Shift mutation hooks
  const [createShift] = useCreateShiftMutation();
  const [updateShift] = useUpdateShiftMutation();
  const [deleteShift] = useDeleteShiftMutation();
  const [setDefaultShift] = useSetDefaultShiftMutation();
  const [assignShiftToEmployee] = useAssignShiftToEmployeeMutation();
  const [unassignShiftFromEmployee] = useUnassignShiftFromEmployeeMutation();

  // Update Redux state when data changes
  useEffect(() => {
    if (departmentsData) {
      dispatch(setDepartments(departmentsData));
    }
  }, [departmentsData, dispatch]);

  useEffect(() => {
    if (designationsData) {
      dispatch(setDesignations(designationsData));
    }
  }, [designationsData, dispatch]);

  useEffect(() => {
    if (branchesData) {
      dispatch(setBranches(branchesData));
    }
  }, [branchesData, dispatch]);

  useEffect(() => {
    if (shiftsData) {
      dispatch(setShifts(shiftsData));
    }
  }, [shiftsData, dispatch]);

  // Get data from Redux store
  const departments = useSelector((state) => state.department.departments);
  const designations = useSelector((state) => state.designation.designations);
  const branches = useSelector((state) => state.branch.branches);
  const shifts = useSelector((state) => state.shift.shifts);

  // Tab configuration
  const tabs = [
    {
      id: 'departments',
      name: t('departments', 'Departments'),
      icon: BuildingOfficeIcon,
      color: 'blue',
      gradient: 'from-cyan-400 to-blue-500',
      data: departments,
      loading: deptLoading,
      error: deptError,
    },
    {
      id: 'designations',
      name: t('designations', 'Designations'),
      icon: BriefcaseIcon,
      color: 'green',
      gradient: 'from-emerald-400 to-green-500',
      data: designations,
      loading: desigLoading,
      error: desigError,
    },
    {
      id: 'branches',
      name: t('branches', 'Branches'),
      icon: UserGroupIcon,
      color: 'purple',
      gradient: 'from-violet-400 to-purple-500',
      data: branches,
      loading: branchLoading,
      error: branchError,
    },
    {
      id: 'shifts',
      name: t('shifts', 'Shifts'),
      icon: ClockIcon,
      color: 'orange',
      gradient: 'from-orange-400 to-red-500',
      data: shifts,
      loading: shiftLoading,
      error: shiftError,
    },
  ];

  const activeTabData = tabs.find((tab) => tab.id === activeTab);

  // Form handlers with enhanced branch handling
  const handleAdd = () => {
    setFormData(getInitialFormData(activeTab));
    setShowAddModal(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({ ...item });
    setShowEditModal(true);
  };

  const handleDelete = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  // Enhanced form change handler for auto-generation (branch and shift codes)
  const handleFormChange = (field, value) => {
    const updatedData = { ...formData, [field]: value };

    // Auto-generate codes when name changes
    if (field === 'name' && value?.trim()) {
      if (activeTab === 'branches') {
        const generatedCode = generateBranchCode(value, branches);
        updatedData.code = generatedCode;
        console.log('Branch code auto-generated:', generatedCode);
      } else if (activeTab === 'shifts') {
        const generatedCode = generateShiftCode(value, shifts);
        updatedData.code = generatedCode;
        console.log('Shift code auto-generated:', generatedCode);
      }
    }

    setFormData(updatedData);
  };

  const handleSetDefaultShift = async (shift) => {
    try {
      await setDefaultShift(shift._id).unwrap();
      toast.success(`${shift.name} set as default shift successfully`);
      refetchShifts();
    } catch (error) {
      console.error('Set default shift error:', error);
      toast.error('Failed to set default shift. Please try again.');
    }
  };

  const handleAssignShift = (shift) => {
    customToast.info('Assign shift to employees feature coming soon');
  };

  // Geolocation handler
  const handleGetCurrentLocation = async () => {
    setIsGettingLocation(true);
    setLocationError('');

    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser');
      setIsGettingLocation(false);
      return;
    }

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        });
      });

      const { latitude, longitude } = position.coords;

      setFormData({
        ...formData,
        geolocation: {
          ...formData.geolocation,
          latitude: parseFloat(latitude.toFixed(6)),
          longitude: parseFloat(longitude.toFixed(6))
        }
      });

      toast.success('Location coordinates updated successfully');
    } catch (error) {
      let errorMessage = 'Unable to get your location';

      switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage = 'Location access denied. Please enable location permissions.';
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage = 'Location information is unavailable.';
          break;
        case error.TIMEOUT:
          errorMessage = 'Location request timed out.';
          break;
      }

      setLocationError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsGettingLocation(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (showAddModal) {
        switch (activeTab) {
          case 'departments':
            await createDepartment(formData).unwrap();
            toast.success('Department created successfully');
            refetchDepartments();
            break;
          case 'designations':
            await createDesignation(formData).unwrap();
            toast.success('Designation created successfully');
            refetchDesignations();
            break;
          case 'branches':
            if (!formData.code && formData.name) {
              const generatedCode = generateBranchCode(formData.name, branches);
              formData.code = generatedCode;
            }
            await createBranch(formData).unwrap();
            toast.success('Branch created successfully');
            refetchBranches();
            break;
          case 'shifts':
            await createShift(formData).unwrap();
            toast.success('Shift created successfully');
            refetchShifts();
            break;
        }
      } else if (showEditModal) {
        switch (activeTab) {
          case 'departments':
            await updateDepartment({ id: selectedItem._id, ...formData }).unwrap();
            toast.success('Department updated successfully');
            refetchDepartments();
            break;
          case 'designations':
            await updateDesignation({ id: selectedItem._id, ...formData }).unwrap();
            toast.success('Designation updated successfully');
            refetchDesignations();
            break;
          case 'branches':
            await updateBranch({ id: selectedItem._id, ...formData }).unwrap();
            toast.success('Branch updated successfully');
            refetchBranches();
            break;
          case 'shifts':
            await updateShift({ id: selectedItem._id, ...formData }).unwrap();
            toast.success('Shift updated successfully');
            refetchShifts();
            break;
        }
      }
      closeModals();
    } catch (error) {
      console.error('Submit error:', error);
      const errorMessage = error?.data?.message || error?.message || 'Operation failed';
      toast.error(errorMessage);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      switch (activeTab) {
        case 'departments':
          await deleteDepartment(selectedItem._id).unwrap();
          toast.success('Department deleted successfully');
          refetchDepartments();
          break;
        case 'designations':
          await deleteDesignation(selectedItem._id).unwrap();
          toast.success('Designation deleted successfully');
          refetchDesignations();
          break;
        case 'branches':
          await deleteBranch(selectedItem._id).unwrap();
          toast.success('Branch deleted successfully');
          refetchBranches();
          break;
        case 'shifts':
          await deleteShift(selectedItem._id).unwrap();
          toast.success('Shift deleted successfully');
          refetchShifts();
          break;
      }
      closeModals();
    } catch (error) {
      console.error('Delete error:', error);
      const errorMessage = error?.data?.message || error?.message || 'Delete failed';
      toast.error(errorMessage);
    }
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedItem(null);
    setFormData({});
  };

  // Get user data from Redux store
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);

  const getInitialFormData = (tab) => {
    const baseData = {
      companyId: user?.companyId || 'default-company-id',
      tenantId: user?.tenantId || 'default-tenant-id',
    };

    switch (tab) {
      case 'departments':
        return {
          name: '',
          description: '',
          status: 'active',
          ...baseData,
        };
      case 'designations':
        return {
          name: '',
          description: '',
          level: 1,
          department: '',
          status: 'active',
          ...baseData,
        };
      case 'branches':
        return {
          name: '',
          code: '',
          address: {
            street: '',
            city: '',
            state: '',
            country: '',
            zipCode: '',
          },
          phone: '',
          email: '',
          timezone: 'UTC',
          isActive: true,
          isHeadOffice: false,
          geolocation: {
            latitude: '',
            longitude: '',
            radius: 100
          },
          ...baseData,
        };
      case 'shifts':
        return {
          name: '',
          code: '',
          startTime: '09:00',
          endTime: '17:00',
          workingHours: 8,
          breaks: [{
            name: 'Lunch Break',
            startTime: '12:00',
            endTime: '13:00',
            isPaid: false
          }],
          gracePeriod: {
            clockIn: 15,
            clockOut: 15
          },
          bufferTime: {
            before: 30,
            after: 120
          },
          isDefault: false,
          isActive: true,
          ...baseData,
        };
      default:
        return baseData;
    }
  };

  const renderForm = () => {
    switch (activeTab) {
      case 'departments':
        return (
          <div className="space-y-6 p-6 bg-gradient-to-r from-blue-50/50 to-cyan-50/50 rounded-xl border border-blue-200/50">
            <div className="flex items-center mb-4">
              <BuildingOfficeIcon className="h-6 w-6 text-blue-600 mr-3" />
              <h3 className="text-lg font-semibold text-blue-900">Department Information</h3>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Department Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  required
                  placeholder="Enter department name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  placeholder="Enter department description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status *
                </label>
                <select
                  value={formData.status || 'active'}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                >
                  <option value="active">✓ Active</option>
                  <option value="inactive">✗ Inactive</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 'designations':
        return (
          <div className="space-y-6 p-6 bg-gradient-to-r from-green-50/50 to-emerald-50/50 rounded-xl border border-green-200/50">
            <div className="flex items-center mb-4">
              <BriefcaseIcon className="h-6 w-6 text-green-600 mr-3" />
              <h3 className="text-lg font-semibold text-green-900">Designation Information</h3>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Designation Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                  required
                  placeholder="Enter designation name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                  placeholder="Enter designation description"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Level *
                  </label>
                  <input
                    type="number"
                    value={formData.level || 1}
                    onChange={(e) => setFormData({ ...formData, level: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    min="1"
                    max="10"
                    placeholder="e.g., 1, 2, 3..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status *
                  </label>
                  <select
                    value={formData.status || 'active'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                  >
                    <option value="active">✓ Active</option>
                    <option value="inactive">✗ Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        );

      case 'branches':
        return (
          <div className="space-y-6 p-6 bg-gradient-to-r from-purple-50/50 to-pink-50/50 rounded-xl border border-purple-200/50">
            <div className="flex items-center mb-4">
              <BuildingOfficeIcon className="h-6 w-6 text-purple-600 mr-3" />
              <h3 className="text-lg font-semibold text-purple-900">Branch Information</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Branch Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                  required
                  placeholder="Enter branch name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Branch Code *
                  <span className="text-xs text-purple-600 ml-2 cursor-help" title='Auto-generated from branch name'>?</span>
                </label>
                <input
                  type="text"
                  value={formData.code || ''}
                  onChange={(e) => handleFormChange('code', e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                  required
                  placeholder="e.g., BR2510"
                />
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center mb-4">
                <MapPinIcon className="h-5 w-5 text-purple-600 mr-2" />
                <h4 className="text-md font-medium text-gray-900">Address</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Street *
                  </label>
                  <input
                    type="text"
                    value={formData.address?.street || ''}
                    onChange={(e) => handleFormChange('address', { ...formData.address, street: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                    required
                    placeholder="Street address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City *
                  </label>
                  <input
                    type="text"
                    value={formData.address?.city || ''}
                    onChange={(e) => handleFormChange('address', { ...formData.address, city: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                    required
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State *
                  </label>
                  <input
                    type="text"
                    value={formData.address?.state || ''}
                    onChange={(e) => handleFormChange('address', { ...formData.address, state: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                    required
                    placeholder="State/Province"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country *
                  </label>
                  <input
                    type="text"
                    value={formData.address?.country || ''}
                    onChange={(e) => handleFormChange('address', { ...formData.address, country: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                    required
                    placeholder="Country"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ZIP Code *
                  </label>
                  <input
                    type="text"
                    value={formData.address?.zipCode || ''}
                    onChange={(e) => handleFormChange('address', { ...formData.address, zipCode: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                    required
                    placeholder="ZIP/Postal code"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center mb-4">
                <UserGroupIcon className="h-5 w-5 text-purple-600 mr-2" />
                <h4 className="text-md font-medium text-gray-900">Contact Information</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone || ''}
                    onChange={(e) => handleFormChange('phone', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => handleFormChange('email', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                    placeholder="contact@branch.com"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <MapPinIcon className="h-5 w-5 text-purple-600 mr-2" />
                  <h4 className="text-md font-medium text-gray-900">Geolocation Settings</h4>
                </div>
                <button
                  type="button"
                  onClick={handleGetCurrentLocation}
                  disabled={isGettingLocation}
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white font-semibold rounded-xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 hover:from-purple-600 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGettingLocation ? (
                    <>
                      <ClockIcon className="w-4 h-4 mr-2 animate-spin" />
                      Getting Location...
                    </>
                  ) : (
                    <>
                      <MapPinIcon className="w-4 h-4 mr-2" />
                      Get Current Location
                    </>
                  )}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Latitude *
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={formData.geolocation?.latitude || ''}
                    onChange={(e) => handleFormChange('geolocation', { ...formData.geolocation, latitude: parseFloat(e.target.value) || '' })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                    placeholder="-90 to 90"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Longitude *
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={formData.geolocation?.longitude || ''}
                    onChange={(e) => handleFormChange('geolocation', { ...formData.geolocation, longitude: parseFloat(e.target.value) || '' })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                    placeholder="-180 to 180"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Radius (meters) *
                  </label>
                  <input
                    type="number"
                    min="10"
                    max="1000"
                    value={formData.geolocation?.radius || 100}
                    onChange={(e) => handleFormChange('geolocation', { ...formData.geolocation, radius: parseInt(e.target.value) || '' })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                    placeholder="100"
                    required
                  />
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                <div className="flex">
                  <ExclamationTriangleIcon className="w-6 h-6 text-purple-400 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-purple-700">
                    <p className="font-semibold mb-3">📍 How to set branch location:</p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Click "Get Current Location" to use your current position</li>
                      <li>Or manually enter latitude/longitude coordinates</li>
                      <li>Set radius (10-1000 meters) for employee attendance validation</li>
                      <li>Enable location permissions in your browser for best results</li>
                    </ul>
                  </div>
                </div>
              </div>

              {locationError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex">
                    <ExclamationTriangleIcon className="w-5 h-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" />
                    <p className="text-sm text-red-700">{locationError}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive || false}
                  onChange={(e) => handleFormChange('isActive', e.target.checked)}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="ml-3 text-sm text-gray-700 font-medium">✓ Active Branch</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isHeadOffice || false}
                  onChange={(e) => handleFormChange('isHeadOffice', e.target.checked)}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="ml-3 text-sm text-gray-700 font-medium">🏢 Head Office</span>
              </label>
            </div>
          </div>
        );

      case 'shifts':
        return (
          <div className="space-y-6 p-6 bg-gradient-to-r from-orange-50/50 to-red-50/50 rounded-xl border border-orange-200/50">
            <div className="flex items-center mb-6">
              <ClockIcon className="h-6 w-6 text-orange-600 mr-3" />
              <h3 className="text-lg font-semibold text-orange-900">Shift Information</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shift Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                  required
                  placeholder="e.g., Morning Shift"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shift Code *
                  <span className="text-xs text-orange-600 ml-2 cursor-help" title='Auto-generated from shift name'>?</span>
                </label>
                <input
                  type="text"
                  value={formData.code || ''}
                  onChange={(e) => handleFormChange('code', e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                  required
                  placeholder="e.g., MS001"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Time *
                </label>
                <input
                  type="time"
                  value={formData.startTime || ''}
                  onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Time *
                </label>
                <input
                  type="time"
                  value={formData.endTime || ''}
                  onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Working Hours *
                </label>
                <input
                  type="number"
                  value={formData.workingHours || 8}
                  onChange={(e) => setFormData({ ...formData, workingHours: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                  min="1"
                  max="24"
                  required
                  placeholder="8"
                />
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center mb-4">
                <ClockIcon className="h-5 w-5 text-orange-600 mr-2" />
                <h4 className="text-md font-medium text-gray-900">Shift Settings</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Clock In Grace Period (mins)
                    <span className="text-xs text-orange-600 ml-2 cursor-help" title='Allowed time after shift start before marking late (minutes)'>?</span>
                  </label>
                  <input
                    type="number"
                    value={formData.gracePeriod?.clockIn || 15}
                    onChange={(e) => setFormData({
                      ...formData,
                      gracePeriod: { ...formData.gracePeriod, clockIn: parseInt(e.target.value) }
                    })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                    min="0"
                    max="120"
                    placeholder="15"
                    title="Time allowed after shift start before marking as late (in minutes)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Clock Out Grace Period (mins)
                    <span className="text-xs text-orange-600 ml-2 cursor-help" title='Allowed time before shift end to clock out (minutes)'>?</span>
                  </label>
                  <input
                    type="number"
                    value={formData.gracePeriod?.clockOut || 15}
                    onChange={(e) => setFormData({
                      ...formData,
                      gracePeriod: { ...formData.gracePeriod, clockOut: parseInt(e.target.value) }
                    })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                    min="0"
                    max="120"
                    placeholder="15"
                    title="Time allowed before shift end to complete clock out (in minutes)"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Early Arrival Buffer (mins)
                    <span className="text-xs text-orange-600 ml-2 cursor-help" title='Minimum time before shift start to allow check-in (minutes)'>?</span>
                  </label>
                  <input
                    type="number"
                    value={formData.bufferTime?.before || 30}
                    onChange={(e) => setFormData({
                      ...formData,
                      bufferTime: { ...formData.bufferTime, before: parseInt(e.target.value) }
                    })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                    min="0"
                    max="240"
                    placeholder="30"
                    title="Minimum time before shift start that employees can check in (in minutes)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Late Departure Buffer (mins)
                    <span className="text-xs text-orange-600 ml-2 cursor-help" title='Additional time after shift end for clock out (minutes)'>?</span>
                  </label>
                  <input
                    type="number"
                    value={formData.bufferTime?.after || 120}
                    onChange={(e) => setFormData({
                      ...formData,
                      bufferTime: { ...formData.bufferTime, after: parseInt(e.target.value) }
                    })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                    min="0"
                    max="480"
                    placeholder="120"
                    title="Additional time after shift end that allows late departure (in minutes)"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive || true}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="ml-3 text-sm text-gray-700 font-medium">✓ Active Shift</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isDefault || false}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="ml-3 text-sm text-gray-700 font-medium">⭐ Default Shift</span>
              </label>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderCards = () => {
    if (!activeTabData?.data) return null;

    const filteredData = activeTabData.data.filter((item) => {
      if (!searchTerm) return true;

      const searchLower = searchTerm.toLowerCase();

      switch (activeTab) {
        case 'departments':
          return (
            item.name?.toLowerCase().includes(searchLower) ||
            item.description?.toLowerCase().includes(searchLower) ||
            item.status?.toLowerCase().includes(searchLower)
          );
        case 'designations':
          return (
            item.name?.toLowerCase().includes(searchLower) ||
            item.description?.toLowerCase().includes(searchLower) ||
            item.level?.toString().includes(searchLower) ||
            item.status?.toLowerCase().includes(searchLower)
          );
        case 'branches':
          return (
            item.name?.toLowerCase().includes(searchLower) ||
            item.code?.toLowerCase().includes(searchLower) ||
            item.address?.street?.toLowerCase().includes(searchLower) ||
            item.address?.city?.toLowerCase().includes(searchLower) ||
            item.address?.state?.toLowerCase().includes(searchLower) ||
            item.address?.country?.toLowerCase().includes(searchLower) ||
            item.phone?.toLowerCase().includes(searchLower) ||
            item.email?.toLowerCase().includes(searchLower)
          );
        case 'shifts':
          return (
            item.name?.toLowerCase().includes(searchLower) ||
            item.code?.toLowerCase().includes(searchLower) ||
            item.startTime?.includes(searchLower) ||
            item.endTime?.includes(searchLower) ||
            item.workingHours?.toString().includes(searchLower) ||
            (item.isActive ? 'active' : 'inactive').includes(searchLower)
          );
        default:
          return true;
      }
    });

    if (filteredData.length === 0) {
      return (
        <div className="text-center py-20">
          <BuildingOfficeIcon className="mx-auto w-32 h-32 text-gray-400 mb-6" />
          <h3 className="text-2xl font-bold text-gray-800 mb-3">
            No {activeTab} found
          </h3>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            Get started by creating your first {activeTab.slice(0, -1)}.
          </p>
          <button
            onClick={handleAdd}
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 hover:from-blue-600 hover:to-purple-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add {activeTab.slice(0, -1)}
          </button>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 auto-rows-fr">
        {filteredData.map((item, index) => {
          const IconComponent = activeTabData.icon;
          const sizes = ['large', 'medium', 'small'];
          const size = sizes[index % 3];

          return (
            <div
              key={item._id}
              className={`${size === 'large' ? 'col-span-1 md:col-span-2 lg:col-span-2 row-span-1' : 'col-span-1'} group relative overflow-hidden`}
            >
              <div className="relative h-full transform transition-all duration-500 hover:scale-105 hover:rotate-1">
                <div className="absolute inset-0 bg-white/90 backdrop-blur-xl rounded-3xl border border-gray-200/50 shadow-xl"></div>

                <div className={`absolute inset-0 bg-gradient-to-br ${activeTabData.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500 rounded-3xl`}></div>

                <div className="absolute inset-0 bg-gray-50 opacity-20 rounded-3xl"></div>

                <div className="absolute top-4 right-4 w-2 h-2 bg-current opacity-20 rounded-full animate-ping"></div>
                <div className="absolute bottom-6 left-6 w-1 h-1 bg-current opacity-30 rounded-full animate-pulse"></div>

                <div className="relative p-6 h-full flex flex-col border-l-4 border-transparent group-hover:border-current transition-colors duration-300">
                  <div className="flex items-start justify-between mb-6">
                    <div className={`p-4 bg-gradient-to-br ${activeTabData.gradient} rounded-2xl shadow-lg transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12 border border-white/20`}>
                      <IconComponent className="h-7 w-7 text-white" />
                    </div>

                    <div className={`px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full border border-gray-200/50 shadow-lg text-gray-700`}>
                      <span className="text-xs font-bold tracking-wide">
                        ● {item.isActive !== undefined ? (item.isActive ? 'ACTIVE' : 'INACTIVE') : (item.status === 'active' ? 'ACTIVE' : 'INACTIVE')}
                      </span>
                    </div>
                  </div>

                  <div className="flex-1 space-y-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 group-hover:text-gray-900 transition-colors duration-300 leading-tight">
                        {item.name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                        {activeTab === 'branches' ? `${item.code} • ${item.address?.city}, ${item.address?.country}` :
                         activeTab === 'shifts' ? `${item.code} • ${item.startTime} - ${item.endTime} (${item.workingHours}h)` :
                         item.description || 'No description'}
                      </p>
                    </div>

                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 bg-gradient-to-r ${activeTabData.gradient} rounded-full animate-pulse`}></div>
                      <div className="w-8 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                      <div className={`w-2 h-2 bg-gradient-to-r ${activeTabData.gradient} rounded-full animate-pulse`}></div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-6 border-t border-gray-200/50">
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleEdit(item)}
                        className={`p-3 bg-gradient-to-br ${activeTabData.gradient} rounded-xl shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300 text-white`}
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="p-3 bg-red-500/80 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300 border border-white/20"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4 text-white" />
                      </button>
                    </div>

                    <div className="text-xs text-gray-400 font-mono bg-gray-50/80 px-3 py-1 rounded-lg backdrop-blur-sm border border-gray-200/30">
                      {item._id.slice(-6)}
                    </div>
                  </div>
                </div>

                <div className={`absolute inset-0 bg-gradient-to-br ${activeTabData.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-3xl pointer-events-none`}></div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
              Master Data
            </h1>
            <p className="mt-2 text-gray-600 text-lg">
              Manage your organization data
            </p>
          </div>
          <div className="mt-6 sm:mt-0">
            <button
              onClick={handleAdd}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 hover:from-blue-600 hover:to-purple-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              {t('add')} {activeTabData?.name}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 mb-8">
          <div className="border-b border-white/30">
            <nav className="flex space-x-8 px-8 py-4" aria-label="Tabs">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center py-3 px-4 rounded-2xl font-semibold text-sm transition-all duration-300 transform hover:scale-105 ${
                      activeTab === tab.id
                        ? `bg-gradient-to-r ${tab.gradient} text-white shadow-lg`
                        : 'text-gray-600 hover:text-gray-800 hover:bg-white/50'
                    }`}
                  >
                    <IconComponent className="h-5 w-5 mr-3" />
                    {tab.name}
                    {tab.data && (
                      <span className={`ml-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                        activeTab === tab.id
                          ? 'bg-white/20 text-white'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {tab.data.length}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Statistics Cards */}
            {activeTabData?.data && activeTab === 'branches' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-cyan-400/20 to-blue-600/20 backdrop-blur-xl border border-white/20 shadow-2xl p-6 transform hover:scale-105 transition-all duration-300">
                  <div className="absolute top-4 right-4 w-3 h-3 bg-cyan-400 rounded-full animate-pulse"></div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-cyan-700 text-sm font-semibold mb-1">
                        Total Branches
                      </p>
                      <p className="text-3xl font-bold text-gray-800">
                        {activeTabData.data.length}
                      </p>
                      <p className="text-xs text-cyan-600 mt-1">
                        All branches
                      </p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl shadow-lg">
                      <UserGroupIcon className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-400 to-blue-500"></div>
                </div>

                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-400/20 to-green-600/20 backdrop-blur-xl border border-white/20 shadow-2xl p-6 transform hover:scale-105 transition-all duration-300">
                  <div className="absolute top-4 right-4 w-3 h-3 bg-emerald-400 rounded-full animate-pulse"></div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-emerald-700 text-sm font-semibold mb-1">Active Branches</p>
                      <p className="text-3xl font-bold text-gray-800">
                        {activeTabData.data.filter(item => item.isActive).length}
                      </p>
                      <p className="text-xs text-emerald-600 mt-1">Currently active</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-emerald-400 to-green-500 rounded-2xl shadow-lg">
                      <CheckIcon className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-green-500"></div>
                </div>

                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-400/20 to-purple-600/20 backdrop-blur-xl border border-white/20 shadow-2xl p-6 transform hover:scale-105 transition-all duration-300">
                  <div className="absolute top-4 right-4 w-3 h-3 bg-violet-400 rounded-full animate-pulse"></div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-violet-700 text-sm font-semibold mb-1">Head Offices</p>
                      <p className="text-3xl font-bold text-gray-800">
                        {activeTabData.data.filter(item => item.isHeadOffice).length}
                      </p>
                      <p className="text-xs text-violet-600 mt-1">Main branches</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-violet-400 to-purple-500 rounded-2xl shadow-lg">
                      <BuildingOfficeIcon className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-violet-400 to-purple-500"></div>
                </div>
              </div>
            )}

            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <div className="absolute inset-0 bg-white/80 backdrop-blur-xl rounded-2xl border border-white/20 shadow-xl"></div>

                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <div className="p-2 bg-gradient-to-br from-blue-400/20 to-purple-600/20 backdrop-blur-sm rounded-xl border border-white/20">
                      <MagnifyingGlassIcon className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>

                  <input
                    type="text"
                    placeholder={`Search ${activeTabData?.name}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="relative block w-full pl-16 pr-12 py-3 bg-transparent border-0 rounded-2xl focus:outline-none focus:ring-0 text-sm font-medium text-gray-800 placeholder-gray-500"
                  />

                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center"
                    >
                      <div className="p-2 bg-red-500/20 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-red-500/30 transition-all duration-200">
                        <XMarkIcon className="h-4 w-4 text-red-600" />
                      </div>
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-4 sm:mt-0 flex items-center space-x-3">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="relative inline-flex items-center px-4 py-3 bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl text-sm font-medium text-gray-700 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                >
                  <div className="p-1 bg-gradient-to-br from-purple-400/20 to-pink-600/20 backdrop-blur-sm rounded-lg border border-white/20 mr-2">
                    <FunnelIcon className="h-4 w-4 text-purple-600" />
                  </div>
                  Filters
                </button>
              </div>
            </div>

            {/* Search Results Indicator */}
            {searchTerm && activeTabData?.data && (
              <div className="mb-4">
                <div className="inline-flex items-center px-4 py-2 bg-blue-50/80 backdrop-blur-sm border border-blue-200/50 rounded-2xl text-sm text-blue-700 font-medium">
                  <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                  Showing {activeTabData.data.filter((item) => {
                    const searchLower = searchTerm.toLowerCase();
                    switch (activeTab) {
                      case 'branches':
                        return (
                          item.name?.toLowerCase().includes(searchLower) ||
                          item.code?.toLowerCase().includes(searchLower) ||
                          item.address?.street?.toLowerCase().includes(searchLower) ||
                          item.address?.city?.toLowerCase().includes(searchLower) ||
                          item.address?.state?.toLowerCase().includes(searchLower) ||
                          item.address?.country?.toLowerCase().includes(searchLower)
                        );
                      default:
                        return item.name?.toLowerCase().includes(searchLower);
                    }
                  }).length} of {activeTabData.data.length} {activeTab}
                </div>
              </div>
            )}

            {/* Cards */}
            {activeTabData?.loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <span className="ml-2 text-secondary-600">Loading...</span>
              </div>
            ) : activeTabData?.error ? (
              <div className="text-center py-12">
                <div className="text-error-600 mb-2">Error loading data</div>
                <button
                  onClick={() => window.location.reload()}
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  Try Again
                </button>
              </div>
            ) : (
              renderCards()
            )}
          </div>
        </div>

        {/* Add/Edit Modal */}
        {(showAddModal || showEditModal) && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-secondary-900 opacity-75"></div>
              </div>

              <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-display font-semibold text-secondary-900">
                      {showAddModal ? t('add') : t('edit')} {activeTabData?.name}
                    </h3>
                    <button
                      onClick={closeModals}
                      className="text-secondary-400 hover:text-secondary-600 transition-colors duration-200"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <form onSubmit={handleSubmit}>
                    {renderForm()}

                    <div className="mt-6 flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={closeModals}
                        className="px-4 py-2 border border-secondary-300 rounded-lg text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
                      >
                        {t('cancel')}
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
                      >
                        {showAddModal ? t('add') : t('update')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-secondary-900 opacity-75"></div>
              </div>

              <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-error-100 rounded-full flex items-center justify-center">
                      <TrashIcon className="h-6 w-6 text-error-600" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-display font-semibold text-secondary-900">
                        Delete {activeTabData?.name}
                      </h3>
                      <p className="text-sm text-secondary-500">
                        Are you sure you want to delete {selectedItem?.name}?
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={closeModals}
                      className="px-4 py-2 border border-secondary-300 rounded-lg text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmDelete}
                      className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-error-600 hover:bg-error-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-error-500 transition-all duration-200"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
};

export default Master;
